import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addTicketingSet, removeTicketingSet, updateTicketingSet } from "../../../../../../../../store/actions/admin/create-event";
import { EventsState } from "../../../../../../../../store/types";
import { GetDefaultTicketingSet } from "../../../../../../../../store/utils/create-event";
import { RegistrationStepType, TicketingSet } from "../../../../../../../../types/working-model";
import Icon, { COLORS, ICONS } from "../../../../../../../general-ui/icon";
import ModalComponent from "../../../../../../../general-ui/modal/modal";
import { Popover } from "../../../../../../../general-ui/popover/popover";
import TextInput, { Validation } from "../../../../../../../general-ui/text-input/text";
import "react-datepicker/dist/react-datepicker.css";
import './paid-ticketing.scss';
import { getStripeStatus } from "../../../../../../../../store/actions/admin/integrations";
import SessionSelectInput from "../../../../../../../general-ui/select/session-select";

const PaidTicketingSettings: React.FC = () => {
	const {
		CreateEventReducer: {
			workingEvent,
		},
		// IntegrationsReducer: {
		// 	stripe
		// },
		AuthReducer: {
			token
		}
	} = useSelector((state: EventsState) => state);
	const dispatch = useDispatch();
	const [workingTicketSet, setWorkingTicketSet] = useState<TicketingSet | null>(null);
	const [workingTicketSetIndex, setWorkingTicketSetIndex] = useState(-1);
	const [ticketingModalOpen, setTicketingModalOpen] = useState(false);
	const [openPopoverIndex, setOpenPopoverIndex] = useState(-1);
	const [errors, setErrors] = useState<any | null>({});

	//the tickets themselves appear 
	const paidTicketingStep = workingEvent?.registration_steps?.find(step => step.type === RegistrationStepType.ticketing);

	const ticketSets = paidTicketingStep?.ticketing;

	const addTicketingType = useCallback(() => {
		dispatch(addTicketingSet(GetDefaultTicketingSet()));
	}, [dispatch]);

	const removeTicketSet = useCallback((index: number) => {
		return () => {
			dispatch(removeTicketingSet(index));
			setOpenPopoverIndex(-1);
		};
	}, [dispatch]);

	const editTicketSet = useCallback((index: number) => {
		return () => {
			setWorkingTicketSet(ticketSets![index]);
			setWorkingTicketSetIndex(index);
			setTicketingModalOpen(true);
			setOpenPopoverIndex(-1);
		};
	}, [ticketSets]);

	const openPopover = useCallback((index: number) => {
		return () => setOpenPopoverIndex(index);
	}, []);

	const closeModal = useCallback(() => {
		setTicketingModalOpen(false);
	}, []);

	const saveWorkingTicketSet = useCallback(() => {
		if (!workingTicketSet) {
			closeModal();

			return;
		}

		if (Object.keys(errors).length > 0) {
			return false;
		}

		dispatch(updateTicketingSet(workingTicketSet, workingTicketSetIndex));

		closeModal();
	}, [workingTicketSet, workingTicketSetIndex, dispatch, closeModal, errors]);

	const setTextValue = ({ target: { value, name } }: React.ChangeEvent<HTMLInputElement>) => {
		setWorkingTicketSet(workingTicketSet => {
			if (!workingTicketSet) {
				return null;
			}

			setErrors((errors: any) => {
				if (name === 'name' && value.length === 0) {
					return {
						...errors,
						[name]: true,
					};
				} else {
					const _errors = errors;
					delete _errors[name];

					return _errors;
				}
			});

			return {
				...workingTicketSet,
				[name]: value,
			};
		});
	};

	const setNumericValue = ({ target: { valueAsNumber, name } }: React.ChangeEvent<HTMLInputElement>) => {
		setWorkingTicketSet(workingTicketSet => {
			if (!workingTicketSet) {
				return null;
			}

			setErrors((errors: any) => {
				if ((name == 'price' && valueAsNumber < 0.5) || (name == 'quantity' && valueAsNumber <= 0)) {
					return {
						...errors,
						[name]: true,
					};
				} else {
					const _errors = errors;
					delete _errors[name];

					return _errors;
				}
			});

			return {
				...workingTicketSet,
				[name]: valueAsNumber,
			};
		});
	};

	const setSessionIds = (sessions: number[]) => {
		const selectedSessionUuids = workingEvent?.sessions.filter(session => sessions.indexOf(session.session) >= 0).map(session => session.uuid) || [];
		// setSessionUuids(selectedSessionUuids);

		setWorkingTicketSet(workingTicketSet => {
			if (!workingTicketSet) {
				return null;
			}

			return {
				...workingTicketSet,
				sessions: sessions,
				session_uuids: selectedSessionUuids,
			};
		});
	};

	useEffect(() => {
		function handleFocus() {
			dispatch(getStripeStatus(token!));
		}

		handleFocus();
		window.addEventListener('focus', handleFocus);

		return () => window.removeEventListener('focus', handleFocus);
	}, [dispatch, token]);

	return (
		<>
			<div className="panel-body">
				<div className="paid-ticketing-header">
					<label>Ticket types</label>
					<button className="no-style" onClick={addTicketingType}>
						<Icon name={ICONS.ADD} size={14} color={COLORS.CYAN} />
						Add ticket type
					</button>
				</div>
				{paidTicketingStep?.ticketing?.map((ticketSet, index) => (
					<div key={`${ticketSet.name}.${index}`} className="paid-ticketing-card">
						<div className="ticket-data">

							<label>{ticketSet.name}</label>
							<span>{ticketSet.quantity} tickets • ${ticketSet.price.toFixed(2)}</span>
						</div>
						<div className="ticket-popover">
							<button onClick={openPopover(index)} className="no-style open-popover">
								<Icon name={ICONS.THREE_DOTS_HORIZONTAL} size={18} color={COLORS.WHITE} />
							</button>
							<Popover
								open={openPopoverIndex === index}
							>
								<div style={{ display: 'flex', flexDirection: 'column' }}>
									<button onClick={editTicketSet(index)}>Edit</button>
									<button onClick={removeTicketSet(index)}>Delete</button>
								</div>

							</Popover>
						</div>
					</div>
				))}
				{/* FUTURE DEV - Integrations are not included in MVP */}
				{/* <div className="paid-ticketing-header" style={{ marginTop: 28 }}>
					<label>Payment method</label>
				</div>

				<div className="stripe-status">
					{stripe.status === StripeStatus.ready ? (
						<>
							<p>Stripe is connected</p>
							<a href={"/admin/settings/integrations"} className="button primary" target="_blank" rel="noreferrer">Go to Integrations</a>
						</>
					) : (
						<>
							<p>Connect your Stripe account to collect payments from your events</p>
							<a href={"/admin/settings/integrations"} className="button primary" target="_blank" rel="noreferrer">Connect with Stripe</a>
						</>
					)}
				</div> */}
			</div>

			<ModalComponent
				closeable={false}
				cancellable={false}
				open={ticketingModalOpen}
				onRequestClose={closeModal}
				title={"Add tickets"}
				subtitle={"You can create multiple ticket types to sell"}
				footer={(
					<>
						<button onClick={closeModal}>Cancel</button>
						<button onClick={saveWorkingTicketSet} className="primary">
							Save
						</button>
					</>
				)}
			>
				<div className="paid-ticketing-modal">
					<div className="paid-ticketing-settings">
						<TextInput
							defaultValue={workingTicketSet?.name ?? ''}
							label="Name"
							onChange={setTextValue}
							style={{ gridArea: "name" }}
							id="ticket_name"
							name="name"
							required
							valid={errors.name ? Validation.error : Validation.normal}
						/>
						<TextInput
							defaultValue={workingTicketSet?.description ?? ''}
							label="Description"
							onChange={setTextValue}
							style={{ gridArea: "description" }}
							id="ticket_description"
							name="description"
						/>
						<TextInput
							defaultValue={workingTicketSet?.price ?? ''}
							label="Price"
							numeric={true}
							onChange={setNumericValue}
							min={0.5}
							max={99999.99}
							step={0.01}
							style={{ gridArea: "price" }}
							id="ticket_price"
							name="price"
							required
							valid={errors.price ? Validation.error : Validation.normal}
						/>
						<TextInput
							defaultValue={"USD"}
							label="Currency"
							disabled={true}
							style={{ gridArea: "currency" }}
							id="ticket_currency"
							name="currency"
						/>
						<TextInput
							defaultValue={workingTicketSet?.quantity ?? ''}
							label="Quantity"
							numeric={true}
							onChange={setNumericValue}
							min={0}
							style={{ gridArea: "quantity" }}
							id="ticket_quantity"
							name="quantity"
							required
							valid={errors.quantity ? Validation.error : Validation.normal}
						/>
						<div style={{ gridArea: "session" }} className="field-group">
							<SessionSelectInput
								placeholder="Select Session"
								sessions={workingEvent!.sessions}
								onChange={setSessionIds}
								preSelectedSessions={workingTicketSet?.sessions ?? []}
							/>
						</div>
					</div>
				</div>
			</ModalComponent>
		</>
	);
};

export default PaidTicketingSettings;